/**
 * Plain-language explanations for every field on the Cash, Shift and General
 * Sales reports (CWS-1418). Each entry says what the number is, how it is
 * worked out, where it comes from and what is left out.
 *
 * Keep this file in step with `docs/reports-metric-definitions.md`, which is
 * the reviewed source of truth for the copy, and with the SQL it describes:
 *   - Cash report:  svc-employee `employeecashReconciliationModel.js`
 *   - Shift report: svc-employee `shiftReportModel.js`
 *   - General Sales: svc-analytics `models/reports/generalSales/reportScope.js`
 *
 * Material tooltips render plain text, so a blank line ("\n\n") is the only
 * structure available; the bubble style turns it into a paragraph break.
 */

export type ReportTips = Readonly<Record<string, string>>;

const CASH_COLLECTED =
  'Cash taken on orders this employee created on this day.\n\n' +
  'Counts payments with method cash and status succeeded. The day runs midnight to ' +
  'midnight in the location’s timezone and is based on when the order was created. ' +
  'Card payments, refunds and tips are not included.';

const OPENING_BALANCE =
  'Float handed to the employee at the start of the shift.\n\n' +
  'It counts towards expected closing only once the hand-over was marked sent or ' +
  'received. Until then it is treated as $0.';

const EXTRA_CASH_ADDED =
  'Cash added to the drawer during the shift (top-ups), as entered in cash reconciliation.';

const EXPECTED_CLOSING =
  'What the drawer should hold at close.\n\n' +
  'Opening balance + extra cash added + cash collected.';

const ACTUAL_CLOSING =
  'The amount the employee counted and entered when closing the drawer. Blank until the ' +
  'entry is closed.';

const DELTA =
  'Actual closing − expected closing. Positive is over, negative is short.\n\n' +
  'Shown only once the entry is closed.';

/** Cash Report: per-employee KPIs and the reconciliation table columns. */
export const CASH_REPORT_TIPS: ReportTips = {
  totalCashCollected:
    'Cash collected on every row for this employee in the date range, added up.\n\n' +
    'Each row counts cash payments with status succeeded on orders the employee ' +
    'created that day, in the location’s timezone. Card payments and refunds are ' +
    'not included.',
  totalExpectedClosing:
    'Expected closing on every row for this employee, added up.\n\n' +
    'Each row is opening balance + extra cash added + cash collected. An opening ' +
    'balance counts only once its hand-over was marked sent or received.',
  totalActualClosing:
    'The closing amounts the employee entered, added up.\n\n' +
    'A row that has not been closed yet adds nothing.',
  totalDelta:
    'Actual closing − expected closing, added across the rows. Positive is over, ' +
    'negative is short.\n\n' +
    'Only closed rows (matched or unmatched) count. Rows still pending are left out.',
  recordsIncluded:
    'Number of cash reconciliation entries for this employee in the date range, one ' +
    'per shift day. Entries not yet closed are included.',
  date: 'The shift day the entry belongs to, in the location’s local time.',
  location: 'The location whose drawer this entry reconciles.',
  openingBalance: OPENING_BALANCE,
  extraCashAdded: EXTRA_CASH_ADDED,
  cashCollected: CASH_COLLECTED,
  expectedClosing: EXPECTED_CLOSING,
  actualClosing: ACTUAL_CLOSING,
  delta: DELTA,
};

/** Shift Report: one employee, one calendar day. */
export const SHIFT_REPORT_TIPS: ReportTips = {
  checkIn:
    'First check-in recorded in attendance for this employee on the report date, in ' +
    'the location’s local time.',
  checkOut:
    'Last check-out recorded in attendance for this employee on the report date, in ' +
    'the location’s local time.',
  revenueTotal:
    'Final price of every order this employee created on the report date whose ' +
    'payment status is paid or completed.\n\n' +
    'Dated by order creation time in the location’s timezone. $0 redemption orders ' +
    'count. Card convenience fees are not added and refunds are not taken off, so ' +
    'this is not the Dashboard’s Revenue figure.',
  revenueUnlimited:
    'Final price of membership plan orders sold on this day. Single washes are never ' +
    'counted here, even when the package has an unlimited wash period.',
  revenueSingleWash:
    'Final price of single wash orders. Rewash orders are included and are usually $0.',
  revenuePrepaid:
    'Final price of prepaid wash books sold on this day.\n\n' +
    'Redeeming a prepaid wash is $0 and adds nothing here.',
  revenueAddOns: 'Final price of stand-alone add-on orders sold on this day.',
  openingBalance: OPENING_BALANCE,
  extraCashAdded: EXTRA_CASH_ADDED,
  cashCollected: CASH_COLLECTED + '\n\nThe transaction count is the number of those cash payments.',
  expectedClosing: EXPECTED_CLOSING,
  actualClosing: ACTUAL_CLOSING,
  delta: DELTA,
  cardTransactions:
    'Amount charged on card payments (card, card present, tap to pay, terminal) on ' +
    'orders this employee created on the report date.\n\n' +
    'Includes payments that were later refunded; refunds are taken off in Net credit. ' +
    'A payment with no method recorded is not counted as card here, although the ' +
    'Dashboard counts it as card.',
  cardRefunds:
    'Card money returned on those payments: the refunded amount, or the whole payment ' +
    'when it was fully refunded.',
  netCredit: 'Card transactions − refunds.',
  washPackages:
    'Paid orders this employee created on the report date, grouped by package, plus ' +
    'prepaid washes redeemed by package.\n\n' +
    'Counts orders, not cars: a prepaid book or membership plan sale is one line, ' +
    'and member washes are not listed here.',
  totalWashes:
    'Sum of the package counts above.\n\n' +
    'Because it counts orders, it can include add-ons, membership plans and prepaid ' +
    'books that are not a car going through the wash.',
  prepaidSold: 'Prepaid wash books sold: paid orders of type prepaid wash book.',
  prepaidRedeemed:
    'Prepaid wash credits redeemed on orders this employee created on the report date.',
  prepaidRevenue: 'Final price of the prepaid wash books sold.',
  addOnsSold: 'Stand-alone add-on orders sold.',
  addOnsRevenue: 'Final price of those add-on orders.',
  singleWashSold: 'Single wash orders sold, including rewash orders.',
  singleWashRevenue: 'Final price of those single wash orders.',
  membershipSold: 'Membership plan sale orders.',
  membershipRedeemed:
    'Member washes recorded against orders this employee created on the report date.',
  membershipRevenue: 'Final price of the membership plan orders.',
};

const PAID_ORDER =
  'A paid order is marked paid and has a settled payment (succeeded, paid or partially ' +
  'refunded). Cancelled orders are excluded.';

const CARS_WASHED =
  'Cars that physically went through the wash.\n\n' +
  'One per completed single wash or rewash, one per membership redemption, one per ' +
  'prepaid credit used, and one per fleet wash. Dated by the wash itself in the ' +
  'location’s timezone. Same definition as the Dashboard’s Total Washes.';

const GROSS_SALES =
  'List price of every paid order in the period, before discounts, plus card ' +
  'convenience fees.\n\n' +
  PAID_ORDER;

const NET_SALES =
  'What was actually charged: final price of every paid order plus card convenience ' +
  'fees.\n\n' +
  'Refunds are not taken off here; they appear under Revenue Leakage. Matches the ' +
  'Dashboard’s Revenue figure.';

const AVERAGE_TICKET =
  'Net sales ÷ total cars washed.\n\n' +
  'Member and prepaid washes count as cars but bring no same-day revenue, so a site ' +
  'with many of them will show a low average ticket. N/A when there were no washes.';

const REWASH_IMPACT =
  'Impact is the list price of the wash that was redone: revenue given away rather ' +
  'than collected.';

/**
 * General Sales Report. `kpi` is keyed by the `key` the API sends with each
 * summary card; the rest follow the section they sit in.
 */
export const GENERAL_SALES_TIPS: Readonly<Record<string, ReportTips>> = {
  kpi: {
    cars: CARS_WASHED,
    packages:
      'Number of paid orders in the period: washes, extras, membership plans and ' +
      'prepaid books.\n\n' +
      'Purchases, not cars. A member wash is a car with no purchase; a prepaid book ' +
      'is a purchase with no car yet.',
    'cars-per-day':
      'Total cars washed ÷ calendar days in the period. Every day counts, ' +
      'including days the site was closed.',
    gross: GROSS_SALES,
    net: NET_SALES,
    cash:
      'Final price of paid orders whose settled payment was cash.\n\n' +
      'Convenience fees are not included, since a fee is only charged on card.',
    card:
      'Final price plus convenience fee of paid orders whose settled payment was card.\n\n' +
      'A payment with no method recorded is counted as card, matching the Dashboard.',
    'avg-ticket': AVERAGE_TICKET,
    'membership-revenue':
      'Money billed for memberships this period: plan sale orders plus linked ' +
      'membership payments.\n\n' +
      'Shows N/A when no billing is recorded at all, because $0 would be wrong.',
    'membership-customers':
      'Distinct customers holding a live membership at the end of the period.\n\n' +
      'Live means started before the period ended and not cancelled or lapsed by ' +
      'then. One customer on two plans is one customer. Not filtered by location: ' +
      'memberships are not tied to a location.',
    'attach-rate':
      'Share of washes that had at least one extra sold with them.\n\n' +
      'Washes with an extra ÷ total cars washed.',
    'memberships-purchased':
      'Membership plans sold on this platform during the period.\n\n' +
      'Counts memberships, not customers. Memberships imported from a vendor ' +
      'system are not purchases and are left out. All locations.',
    'memberships-active':
      'Memberships live at the end of the period, from every source.\n\n' +
      'Live means started before the period ended and not cancelled or lapsed by ' +
      'then, the same test the entry gate applies. One customer on two plans is ' +
      'two memberships here. All locations.',
    'memberships-imported':
      'Memberships brought in from a vendor system (for example DRB, EverWash or ' +
      'iWashPOS) that were still live at the end of the period.\n\n' +
      'The hint gives how many were imported in all and how many of those had ' +
      'cancelled or lapsed by then. All locations.',
  },
  volume: {
    totalCarsWashed: CARS_WASHED,
    totalPackagesPurchased: 'Paid orders of any type in the period. Counts purchases, not cars.',
    avgCarsPerDay: 'Total cars washed ÷ calendar days in the period.',
    grossSales: GROSS_SALES,
    netSales: NET_SALES,
    averageTicket: AVERAGE_TICKET,
    dailySales:
      'Gross and net sales per day, week or month, depending on how long the period ' +
      'is. Dated by the order’s appointment time in the location’s timezone.',
    washVolume: 'Cars washed per bucket, using the same definition as Total Cars Washed.',
    averageTicketTrend: 'Net sales ÷ cars washed, recomputed for each bucket from its own totals.',
    grossToNet:
      'How much of gross sales was kept. The difference between gross and net is ' +
      'discounting; refunds are reported under Revenue Leakage instead.',
    difference: 'Gross sales − net sales: the total discounted off list price.',
    revenueBreakdown:
      'Net sales split by what was bought. Extras sold with a wash are moved out of ' +
      'the wash row and into the extras row so nothing is counted twice.',
    breakdownUnits: 'Number of paid items in this category.',
    breakdownRevenue: 'Final price of those items, in this category.',
    breakdownShare: 'This category’s revenue as a share of net sales.',
  },
  revenueCategory: {
    washes: 'Wash revenue with any attached extras stripped out.',
    extras: 'Extras sold with a wash, plus extras sold on their own.',
    memberships: 'Buying or renewing a membership plan.',
    prepaid_books: 'Buying a prepaid book of washes.',
    other: 'Paid orders that fit none of the categories above.',
    fees: 'Card convenience fees, listed separately.',
  },
  packageMix: {
    package:
      'Each wash package sold in the period. Washes with no package are shown as ' +
      'unmapped, and prepaid washes are kept on their own row.',
    units: 'Washes on this package in the period.',
    revenue: 'Final price of those washes.',
    washShare: 'This package’s washes as a share of all washes.',
    revenueShare: 'This package’s revenue as a share of all wash revenue.',
  },
  extras: {
    attachRate:
      'Washes with at least one extra ÷ eligible washes.\n\n' +
      'Extras sold on their own have no wash and do not raise the rate.',
    revenuePerAttachedCar:
      'Extras revenue ÷ washes that had an extra. Divided by attached cars, not ' + 'all washes.',
    totalExtrasRevenue:
      'Final price of every extra sold in the period, with a wash or on its own. Part ' +
      'of net sales.',
    extra: 'Each extra sold in the period.',
    unitsSold: 'Number sold, with a wash or on its own.',
    revenue: 'Final price of those sales.',
    rowAttachRate: 'Washes carrying this extra ÷ eligible washes.',
    rowRevenuePerAttachedCar: 'This extra’s revenue ÷ washes carrying it.',
  },
  customerMix: {
    customerType:
      'How each wash was paid for: retail (paid on the day), membership, prepaid, or ' + 'fleet.',
    washes: 'Cars washed under this customer type.',
    washShare: 'This type’s washes as a share of all washes.',
    revenue:
      'Revenue attributed to this type. Retail is what the order charged; membership ' +
      'spreads the subscription revenue across the washes it covered; prepaid is $0 ' +
      'because the money was taken when the book was sold.',
    revenueShare: 'This type’s revenue as a share of the total.',
    shareOfWashes: 'Split of cars washed by customer type.',
    shareOfRevenue: 'Split of revenue by customer type.',
  },
  customerType: {
    retail: 'Washes paid on the day. Revenue is what the order itself charged.',
    membership:
      'Washes redeemed by members. Revenue is the subscription billing spread across ' +
      'the washes it covered, so members do not look free.',
    prepaid:
      'Washes redeemed from a prepaid book. Revenue is $0 on purpose: the money was ' +
      'counted when the book was sold.',
    fleet: 'Fleet washes. They never create an order, so they carry no revenue here.',
  },
  membership: {
    beginning: 'Memberships live at the start of the period. All locations.',
    newSignups: 'Memberships created during the period. All locations.',
    cancellations:
      'Memberships that stopped during the period, whether someone cancelled or the ' +
      'paid period simply ran out. All locations.\n\n' +
      'Beginning + new signups − this = ending.',
    ending: 'Memberships live at the end of the period. All locations.',
    churn: 'Cancellations and lapses ÷ beginning members.',
    arpm:
      'Average revenue per member: membership revenue billed ÷ average active ' +
      'members, where average active is (beginning + ending) ÷ 2.',
    avgWashesPerMember:
      'Member washes redeemed ÷ average active members, over the number of days ' +
      'shown. Only a monthly figure when the period is a month.',
    captureRate:
      'Distinct customers who started a plan ÷ distinct retail customers who ' +
      'washed in the period. Counting customers on both sides keeps it under 100%.',
    revenueBilled:
      'What members were charged this period: plan sale orders plus linked ' +
      'membership payments. N/A when no billing is recorded.',
    washesRedeemed:
      'Member washes delivered in the period. A separate measure from billing and ' +
      'never netted against it.',
    activeMembers: 'Memberships live on each day of the period.',
    signupsVsDepartures: 'New memberships and memberships that stopped, per bucket.',
  },
  prepaid: {
    booksSold: 'Prepaid wash books bought in the period (paid orders).',
    washesPurchased: 'Total washes the books sold this period contain.',
    revenue:
      'Money taken for those books. Deferred revenue: the washes have not been ' +
      'delivered yet, so it is not wash revenue.',
    washesRedeemed:
      'Prepaid credits used in the period, usually from books bought earlier. Not a ' +
      'redemption rate against books sold.',
    book: 'Each prepaid wash book with sales or redemptions in the period.',
    rowBooksSold: 'Books of this type bought in the period.',
    rowWashesPurchased: 'Washes those books contain.',
    rowRevenue: 'Money taken for those books.',
    rowWashesRedeemed: 'Credits from this book type used in the period.',
  },
  leakage: {
    total:
      'Revenue given away rather than collected: discounts + rewash impact + voids + ' +
      'refunds, and its share of gross sales.',
    discounts:
      'Money discounted off list price on paid orders, by discount type, with the ' +
      'number of orders and share of gross sales.',
    rewashes: 'Free redos of a wash already counted. ' + REWASH_IMPACT,
    voids:
      'Cancelled orders: how many and their list value. They are held out of volume ' +
      'and sales, so this is the only place they appear. Automatic cancellations of ' +
      'unpaid checkouts and orders cancelled as a test are not counted.',
    refunds:
      'Money returned on paid orders. Refunds are not taken off net sales, so this is ' +
      'the only place they appear.',
    discountType: 'The discount or coupon type applied.',
    discountCount: 'Orders that used this discount type.',
    discountAmount: 'Total taken off list price by this discount type.',
    pctOfGross: 'This amount as a share of gross sales.',
    rewashReason: 'The reason recorded when the rewash was given.',
    rewashCount: 'Number of rewashes.',
    rewashImpact: REWASH_IMPACT,
    pctOfRewashes: 'Share of all rewashes in the period.',
    employee: 'The employee who authorised the rewash.',
    adjustmentCategory: 'Voids are cancelled orders; refunds are money returned.',
    adjustmentCount: 'Number of orders in this category.',
    adjustmentAmount: 'Their total value.',
    cancelledOrder: 'The order code of a cancelled order behind the Voids count.',
    cancelledDate: 'The appointment date, in the report’s timezone.',
    cancelledCustomer: 'The customer, or the plate when no name was recorded.',
    cancelledItem: 'What was ordered.',
    cancelledReason: 'The cancellation reason recorded, if any.',
    cancelledBy: 'Who cancelled the order.',
    cancelledRefund: 'Whether any payment on the order was refunded.',
    cancelledAmount: 'The order’s value at the time it was cancelled.',
  },
};
